import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { firestore, firebaseAuth } from '../firebase';
import { useToast } from '../Context/ToastContext';
import { addToCart, removeFromCart } from '../Actions/cartActions';
import Rating from './Rating';

export default function Product(props) {
  const { product } = props;
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [isInCart, setIsInCart] = useState(false);
  
  const history = useHistory();
  const dispatch = useDispatch();
  const { success, error: showError } = useToast();
  
  const userSignin = useSelector(state => state.userSignin);
  const { userInfo } = userSignin;
  const cart = useSelector(state => state.cart);
  const { cartItems } = cart;
  const currentUser = firebaseAuth.currentUser;
  const uid = (authUser && authUser.uid) || (userInfo && userInfo.uid) || (currentUser && currentUser.uid) || null;

  // Initialize Firebase auth listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setAuthUser(user || null);
    });
    return () => unsubscribe();
  }, []);

  // Check if product is in wishlist (with cleanup to avoid setState after unmount)
  useEffect(() => {
    let isActive = true;
    const run = async () => {
      try {
        if (uid && product.id) {
          const wishlistRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_WISHLIST');
          const wishlistDoc = await getDoc(wishlistRef);
          if (!isActive) return;
          if (wishlistDoc.exists()) {
            const data = wishlistDoc.data();
            const size = Number(data.list_size) || 0;
            let found = false;
            for (let i = 0; i < size; i++) {
              if (data[`product_ID_${i}`] === product.id) {
                found = true;
                break;
              }
            }
            setIsInWishlist(found);
          } else {
            setIsInWishlist(false);
          }
        } else {
          setIsInWishlist(false);
        }
      } catch (error) {
        if (!isActive) return;
        console.error('Error checking wishlist status:', error);
      }
    };
    run();
    return () => {
      isActive = false;
    };
  }, [uid, product.id]);

  // Check if product is in cart
  useEffect(() => {
    if (product.id && cartItems) {
      const productInCart = cartItems.find(item => item.product === product.id);
      setIsInCart(!!productInCart);
    }
  }, [product.id, cartItems]);

  const checkWishlistStatus = async () => {
    try {
      const wishlistRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_WISHLIST');
      const wishlistDoc = await getDoc(wishlistRef);
      
      if (wishlistDoc.exists()) {
        const data = wishlistDoc.data();
        const size = Number(data.list_size) || 0;
        
        for (let i = 0; i < size; i++) {
          if (data[`product_ID_${i}`] === product.id) {
            setIsInWishlist(true);
            return;
          }
        }
      }
      setIsInWishlist(false);
    } catch (error) {
      console.error('Error checking wishlist status:', error);
    }
  };

  const toggleWishlist = async () => {
    if (!uid) {
      showError('Please sign in to add items to wishlist');
      return;
    }

    try {
      const wishlistRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_WISHLIST');
      const wishlistDoc = await getDoc(wishlistRef);
      
      if (isInWishlist) {
        // Remove from wishlist
        if (wishlistDoc.exists()) {
          const data = wishlistDoc.data() || {};
          const size = Number(data.list_size) || 0;
          const arr = [];
          
          for (let i = 0; i < size; i++) {
            const pid = data[`product_ID_${i}`];
            if (pid && pid !== product.id) arr.push(pid);
          }
          
          const payload = { list_size: arr.length };
          arr.forEach((pid, idx) => payload[`product_ID_${idx}`] = pid);
          
          await setDoc(wishlistRef, payload, { merge: false });
          setIsInWishlist(false);
          success('Removed from wishlist!');
        }
      } else {
        // Add to wishlist
        const data = wishlistDoc.exists() ? wishlistDoc.data() : {};
        const size = Number(data.list_size) || 0;
        
        const payload = {
          ...data,
          list_size: size + 1,
          [`product_ID_${size}`]: product.id
        };
        
        await setDoc(wishlistRef, payload, { merge: true });
        setIsInWishlist(true);
        success('Added to wishlist!');
      }
      
      // Trigger header count refresh
      if (window.refreshHeaderCounts) {
        window.refreshHeaderCounts();
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      showError('Failed to update wishlist');
    }
  };

  const handleAddToCart = () => {
    if (!uid) {
      showError('Please sign in to add products to cart');
      return;
    }
    
    if (isInCart) {
      // Remove from cart
      dispatch(removeFromCart(product.id));
      success('Product removed from cart!');
      setIsInCart(false);
    } else {
      // Add to cart
      dispatch(addToCart(product.id, 1));
      success('Product added to cart!');
      setIsInCart(true);
    }
  };

  const handleBuyNow = async () => {
    try {
      await dispatch(addToCart(product.id, 1));
      success('Product added to cart!');
      
      // Navigate to shipping page
      try { 
        localStorage.setItem('checkoutItem', JSON.stringify({ productId: product.id, qty: 1 })); 
      } catch (_) {}
      history.push('/shipping');
    } catch (error) {
      console.error('Error adding to cart:', error);
      showError('Failed to add product to cart');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: product.product_title,
      text: `Check out this amazing product: ${product.product_title}`,
      url: `${window.location.origin}/product/${product.id}`
    };

    try {
      if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(shareData.url);
        success('Product link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        success('Product link copied to clipboard!');
      } catch (clipboardError) {
        showError('Failed to share product');
      }
    }
  };

  // Calculate discount
  const currentPrice = Number(product.product_price || 0);
  const originalPrice = Number(product.cutted_price || 0);
  const hasDiscount = originalPrice > currentPrice;
  const discountPercent = hasDiscount ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;

  // Calculate available stock: stock_quantity - totalOrderQty
  const stockQuantity = Number(product.stock_quantity || 0);
  const totalOrderQty = Number(product.totalOrderQty || 0);
  const availableStock = stockQuantity - totalOrderQty;

  return (
    <Link to={`/product/${product.id}`} className="product-card-link" style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="wishlist-item product-card">
        {hasDiscount && (
          <div className="discount-badge">
            {discountPercent}% OFF
          </div>
        )}
        
        <div className="wishlist-image-container product-image-container">
          <button 
            className={`wishlist-heart-btn ${isInWishlist ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWishlist();
            }}
            title={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <i className={`${isInWishlist ? 'fas' : 'far'} fa-heart`}></i>
          </button>
          
          <button 
            className="share-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleShare();
            }}
            title="Share product"
          >
            <i className="fas fa-share-alt"></i>
          </button>
          
          <img
            className="product-image"
            style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', display: 'block', background: '#fff' }}
            src={product.product_image_1}
            alt={product.product_title}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDOTQuNDc3MiA3MCA5MCA3NC40NzcyIDkwIDgwVjEyMEM5MCAxMjUuNTIzIDk0LjQ3NzIgMTMwIDEwMCAxMzBIMTQwQzE0NS41MjMgMTMwIDE1MCAxMjUuNTIzIDE1MCAxMjBWODBDMTUwIDc0LjQ3NzIgMTQ1LjUyMyA3MCA1MCA3MEgxMDBaIiBmaWxsPSIjRTVFN0VCIi8+Cjwvc3ZnPgo=';
            }}
          />
        </div>
        
        <div className="wishlist-product-info product-card-body">
          <h3 className="wishlist-product-title product-title-link">
            {product.product_title}
          </h3>
          
          {product.brand && (
            <div className="wishlist-meta">
              <span>Brand: {product.brand}</span>
            </div>
          )}
          
          <div className="wishlist-meta">
            {(product.category || product.Category) && (
              <span>{product.category || product.Category}</span>
            )}
            {product.rating && (
              <span> {product.rating}</span>
            )}
            {product.cod_available && (
              <span>COD Available</span>
            )}
          </div>
          
          <div className="wishlist-price-container product-price-container">
            <span className="wishlist-price-current product-price">₹{currentPrice.toLocaleString()}</span>
            {hasDiscount && (
              <>
                <span className="wishlist-price-original">₹{originalPrice.toLocaleString()}</span>
                <span className="wishlist-savings">Save ₹{(originalPrice - currentPrice).toLocaleString()}</span>
              </>
            )}
            {/* Stock Status Badge */}
            <span className={`stock-badge-inline ${availableStock > 0 ? 'in-stock' : 'out-of-stock'}`}>
              {availableStock > 0 ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>
          
          <div className="wishlist-actions product-actions">
            <button 
              className={`add-to-cart-btn ${isInCart ? 'in-cart' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddToCart();
              }}
              title={isInCart ? 'Remove from cart' : 'Add to cart'}
            >
              <i className={`fas ${isInCart ? 'fa-check' : 'fa-shopping-cart'}`}></i>
              {isInCart ? 'In Cart' : 'Add to Cart'}
            </button>
            
            <button 
              className="buy-now-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleBuyNow();
              }}
              title="Buy now"
            >
              <i className="fas fa-bolt"></i>
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}